import { useEffect, useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { 
  CreditCard, 
  ArrowLeft, 
  Settings, 
  Building2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Percent,
  RefreshCw,
  ExternalLink,
  Edit,
  Save,
  X
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type StripeAdminSettings, type StripeConnectedAccount } from '../lib/api'

export function AdminStripePage() {
  const navigate = useNavigate()
  const { user, isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const [settings, setSettings] = useState<StripeAdminSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // Edit default fee state
  const [editingDefaultFee, setEditingDefaultFee] = useState(false)
  const [defaultFeeValue, setDefaultFeeValue] = useState('')
  const [savingDefaultFee, setSavingDefaultFee] = useState(false)
  
  // Edit tenant fee modal
  const [editingTenant, setEditingTenant] = useState<StripeConnectedAccount | null>(null)
  const [tenantFeeValue, setTenantFeeValue] = useState('')
  const [tenantTier, setTenantTier] = useState<string>('')
  const [savingTenantFee, setSavingTenantFee] = useState(false)

  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [isLoading, isAuthenticated, navigate])
  
  useEffect(() => {
    if (isAuthenticated && user && !user.isGlobalAdmin) {
      navigate({ to: '/dashboard' })
    }
  }, [isAuthenticated, user, navigate])
  
  useEffect(() => {
    if (isAuthenticated && user?.isGlobalAdmin) {
      loadData()
    }
  }, [isAuthenticated, user])
  
  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.stripe.getAdminSettings()
      setSettings(res)
      setDefaultFeeValue(res.settings.defaultFeePercent)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Stripe settings')
    } finally {
      setLoading(false)
    }
  }
  
  const handleSaveDefaultFee = async () => {
    setSavingDefaultFee(true)
    try {
      await api.stripe.updateAdminSettings({ defaultFeePercent: defaultFeeValue })
      setEditingDefaultFee(false)
      setSuccessMessage('Default fee updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update default fee')
    } finally {
      setSavingDefaultFee(false)
    }
  }
  
  const handleEditTenant = (account: StripeConnectedAccount) => {
    setEditingTenant(account)
    setTenantFeeValue(account.roomsiFeePercent)
    setTenantTier(account.pricingTier)
  }
  
  const handleSaveTenantFee = async () => {
    if (!editingTenant) return
    setSavingTenantFee(true)
    try {
      await api.stripe.updateTenantStripe(editingTenant.orgId, {
        roomsiFeePercent: tenantFeeValue,
        pricingTier: tenantTier,
      })
      setEditingTenant(null)
      setSuccessMessage('Tenant settings updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant settings')
    } finally {
      setSavingTenantFee(false)
    }
  }

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-primary-600" />
                <h1 className="text-xl font-semibold text-gray-900">Stripe Settings</h1>
              </div>
            </div>
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}
        
        {/* Configuration Status */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Configuration</h2>
          
          <div className="flex items-center gap-3 mb-6">
            {settings?.settings.stripeConfigured ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700 font-medium">Stripe is configured</span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-700 font-medium">Stripe is not configured</span>
                <span className="text-gray-500 text-sm">(Add STRIPE_SECRET_KEY to .env)</span>
              </>
            )}
          </div>
          
          {/* Default Fee Setting */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Default Platform Fee</h3>
                <p className="text-sm text-gray-500">Applied to new tenants by default</p>
              </div>
              
              {editingDefaultFee ? (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={defaultFeeValue}
                      onChange={(e) => setDefaultFeeValue(e.target.value)}
                      className="w-24 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                  <button
                    onClick={handleSaveDefaultFee}
                    disabled={savingDefaultFee}
                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingDefaultFee(false)
                      setDefaultFeeValue(settings?.settings.defaultFeePercent || '2.00')
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-semibold text-gray-900">
                    {settings?.settings.defaultFeePercent || '2.00'}%
                  </span>
                  <button
                    onClick={() => setEditingDefaultFee(true)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-gray-500">Total Organisations</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{settings?.totalOrgs || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-gray-500">Connected Accounts</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{settings?.connectedCount || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Percent className="w-5 h-5 text-teal-600" />
              </div>
              <span className="text-gray-500">Connection Rate</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {settings?.totalOrgs 
                ? Math.round((settings.connectedCount / settings.totalOrgs) * 100)
                : 0}%
            </p>
          </div>
        </div>
        
        {/* Connected Accounts Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Connected Stripe Accounts</h2>
          </div>
          
          {settings?.connectedAccounts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No organisations have connected their Stripe accounts yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organisation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pricing Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Platform Fee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {settings?.connectedAccounts.map((account) => (
                    <tr key={account.orgId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="font-medium text-gray-900">{account.orgName}</div>
                          <div className="text-sm text-gray-500">{account.orgSlug}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {account.error ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <XCircle className="w-3 h-3" />
                            Error
                          </span>
                        ) : account.chargesEnabled ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            <AlertTriangle className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          account.pricingTier === 'free' ? 'bg-teal-100 text-teal-700' :
                          account.pricingTier === 'charity' ? 'bg-blue-100 text-blue-700' :
                          account.pricingTier === 'discount' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {account.pricingTier.charAt(0).toUpperCase() + account.pricingTier.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{account.roomsiFeePercent}%</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditTenant(account)}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title="Edit fee settings"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <a
                            href={`https://dashboard.stripe.com/connect/accounts/${account.stripeAccountId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title="View in Stripe"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      
      {/* Edit Tenant Fee Modal */}
      {editingTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Fee Settings
              </h3>
              <button
                onClick={() => setEditingTenant(null)}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Configure pricing for <strong>{editingTenant.orgName}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pricing Tier
                </label>
                <select
                  value={tenantTier}
                  onChange={(e) => {
                    setTenantTier(e.target.value)
                    if (e.target.value === 'free') {
                      setTenantFeeValue('0.00')
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="standard">Standard</option>
                  <option value="charity">Charity</option>
                  <option value="discount">Discount</option>
                  <option value="free">Free (No Platform Fee)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platform Fee Percentage
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={tenantFeeValue}
                    onChange={(e) => setTenantFeeValue(e.target.value)}
                    disabled={tenantTier === 'free'}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
                {tenantTier === 'free' && (
                  <p className="text-sm text-gray-500 mt-1">
                    Free tier organisations don't pay platform fees
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingTenant(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTenantFee}
                disabled={savingTenantFee}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {savingTenantFee ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
